// SupportPortalServlet injects window.__API_BASE__ into every served index.html when it
// can resolve the actual PortalServletContainer servlet's configured WCMS_WebServlet.URL
// (see resolveApiUrl() there) - so the API's real mount path is whatever an admin set up,
// not a hardcoded assumption. Falls back to the historical "/Api sibling of the base path"
// guess if that lookup found nothing, then to VITE_API_BASE for `npm run dev` (where Vite
// serves the bundle directly and nothing injects window.__BASE_PATH__/__API_BASE__ at all).
export const BASE = (typeof window !== 'undefined' && window.__API_BASE__)
  ? window.__API_BASE__
  : (typeof window !== 'undefined' && window.__BASE_PATH__ !== undefined)
    ? `${window.__BASE_PATH__}/Api`
    : (import.meta.env.VITE_API_BASE ?? 'https://api.actfact.com/v1/portal');

// The session token is an HttpOnly cookie no script can read. JS only keeps this marker:
// a random, non-secret id per login, so every tab knows "logged in" and which login.
export const LOGIN_MARKER_KEY = 'af_login';
// Older builds kept the readable token here - removed so it doesn't linger.
const LEGACY_TOKEN_KEY = 'af_token';

export function readLoginMarker() {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  return localStorage.getItem(LOGIN_MARKER_KEY);
}

// Records a new login and returns its marker.
export function storeLoginMarker() {
  const marker = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(LOGIN_MARKER_KEY, marker);
  return marker;
}

// With `onlyIf`, only clears when the marker is still that one - a newer login from
// another tab must survive.
export function clearLoginMarker(onlyIf) {
  if (onlyIf !== undefined && localStorage.getItem(LOGIN_MARKER_KEY) !== onlyIf) return;
  localStorage.removeItem(LOGIN_MARKER_KEY);
}

// Requests are scoped to the session they start in. Every login and logout starts a new
// one: running requests of the old session are aborted, and a 401 that still arrives for
// one of them is ignored instead of ending the new session.
let _onUnauthorized = null;
let _sessionAbort = new AbortController();

export function setOnUnauthorized(callback) {
  _onUnauthorized = callback;
}

export function startNewSession() {
  _sessionAbort.abort();
  _sessionAbort = new AbortController();
}

function reportUnauthorized(sessionSignal) {
  if (!sessionSignal.aborted) _onUnauthorized?.();
}

function combineSignals(own, session) {
  if (!own) return session;
  if (AbortSignal.any) return AbortSignal.any([own, session]);
  const combined = new AbortController();
  for (const s of [own, session]) {
    if (s.aborted) combined.abort();
    else s.addEventListener('abort', () => combined.abort(), { once: true });
  }
  return combined.signal;
}

// Runs a request within the current session. If the session ends mid-way the request is
// aborted and the promise never settles - nobody waits for it anymore, so callers' error
// handling (e.g. "could not load" toasts) must not fire. A caller's own abort still rejects.
export async function runInSession(request) {
  const session = _sessionAbort;
  try {
    return await request(session.signal);
  } catch (err) {
    if (session.signal.aborted) return new Promise(() => {});
    throw err;
  }
}

export async function login(username, password) {
  const res = await fetch(`${BASE}/auth/token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (res.status === 401 || res.status === 403 || res.status === 500) {
    let body = {};
    try { body = await res.json(); } catch {}
    throw new Error(body.detail ?? 'Ongeldige gebruikersnaam of wachtwoord.');
  }
  if (!res.ok) throw new Error(`Server error (${res.status})`);
  const data = await res.json();
  if (data.enrollment_required) {
    return { status: 'enrollment_required', pendingToken: data.pending_token };
  }
  if (data.mfa_required) {
    return { status: 'mfa_required', pendingToken: data.pending_token };
  }
  return { status: 'ok' };
}

async function post2fa(path, payload, fallbackMessage) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch {}
    throw new Error(body.detail ?? fallbackMessage);
  }
  return res.json();
}

export async function verifyTwoFactor(pendingToken, code) {
  await post2fa('/auth/2fa/verify', { pending_token: pendingToken, code },
    'De ingevoerde code is onjuist.');
}

export async function beginTwoFactorEnrollment(pendingToken) {
  const data = await post2fa('/auth/2fa/enroll', { pending_token: pendingToken },
    'Kon geen QR-code genereren.');
  return { secret: data.secret, qrDataUri: data.qr_data_uri };
}

export async function confirmTwoFactorEnrollment(pendingToken, code) {
  await post2fa('/auth/2fa/enroll/confirm', { pending_token: pendingToken, code },
    'De ingevoerde code is onjuist.');
}

// Ends the server session and clears the session cookie - best effort, the caller
// drops to the login page either way.
export async function logout() {
  try {
    await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    // Offline or server down - nothing more to do from here.
  }
}

export function apiFetch(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers ?? {}),
  };
  return runInSession(async (sessionSignal) => {
    const res = await fetch(`${BASE}${path}`, {
      credentials: 'include', ...options, headers, signal: combineSignals(options.signal, sessionSignal),
    });
    if (!res.ok) {
      if (res.status === 401) reportUnauthorized(sessionSignal);
      let body = {};
      try { body = await res.json(); } catch { /* non-JSON error body */ }
      const err = new Error(body.detail ?? `HTTP ${res.status}`);
      err.status = res.status;
      err.body = body;
      throw err;
    }
    const text = await res.text();
    return text.length ? JSON.parse(text) : null;
  });
}

// Fetches a binary resource as a Blob - for blob: URLs (e.g. previews).
export function fetchBlob(path) {
  return runInSession(async (signal) => {
    const res = await fetch(`${BASE}${path}`, { credentials: 'include', signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  });
}

// Reads a fetch Response body as a Blob, reporting fractional progress (0..1) as bytes
// arrive. Falls back to a plain, all-at-once res.blob() when there's no onProgress
// callback, no readable stream, or no Content-Length to compute a fraction from.
export async function readBlobWithProgress(res, onProgress) {
  const total = Number(res.headers.get('Content-Length')) || 0;
  if (!onProgress || !res.body || !total) {
    const blob = await res.blob();
    onProgress?.(1);
    return blob;
  }

  const reader = res.body.getReader();
  const chunks = [];
  let received = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(received / total);
  }

  return new Blob(chunks);
}

export async function downloadFile(path, filename, onProgress) {
  const blob = await runInSession(async (signal) => {
    const res = await fetch(`${BASE}${path}`, { credentials: 'include', signal });
    if (!res.ok) {
      if (res.status === 401) reportUnauthorized(signal);
      throw new Error(`HTTP ${res.status}`);
    }
    return readBlobWithProgress(res, onProgress);
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
